import { DEFAULT_CITY } from './GlobalUtil';
import { DateUtil } from './DateUtil';
import { timeOptions } from '../components/TimePicker';

export interface BrowseFilterState {
  categories: string;
  free: string;
  period: string;
  time: string;
  city: string;
  venue: string;
  latitude: string;
  longitude: string;
}

export interface AppliedFilterChip {
  key: 'categories' | 'free' | 'period' | 'time' | 'location' | 'venue';
  label: string;
  value?: string;
}

export function getDefaultBrowseFilters(): BrowseFilterState {
  return {
    categories: '',
    free: '0',
    period: DateUtil.formatDateToLocalYMD(new Date()),
    time: timeOptions.TOUTE_HEURE,
    city: DEFAULT_CITY,
    venue: '',
    latitude: '',
    longitude: '',
  };
}

export function getNormalizedCategories(categories: string): string[] {
  return categories.split(';').filter(Boolean);
}

export function isDefaultPeriod(period: string): boolean {
  return period === getDefaultBrowseFilters().period;
}

export function getPeriodLabel(period: string): string {
  if (!period || isDefaultPeriod(period)) {
    return '';
  }

  if (period === 'thisWeek') {
    return 'Cette semaine';
  }

  if (period === 'nextWeek') {
    return 'Semaine prochaine';
  }

  if (/^\d+$/.test(period)) {
    return DateUtil.getFrenchMonthName((Number(period) + 1).toString());
  }

  if (DateUtil.isValidDateString(period)) {
    const date = DateUtil.createLocalDateFromYMD(period);
    return date.toLocaleDateString('fr-CA', {
      day: 'numeric',
      month: 'long',
    });
  }

  return period;
}

export function getTimeLabel(time: string): string {
  if (!time || time === getDefaultBrowseFilters().time) {
    return '';
  }

  return time;
}

export function getAppliedFilterChips(
  filters: BrowseFilterState,
  categoryNameBySlug: Map<string, string>,
  venueNameBySlug: Map<string, string> = new Map()
): AppliedFilterChip[] {
  const chips: AppliedFilterChip[] = [];

  for (const slug of getNormalizedCategories(filters.categories)) {
    chips.push({
      key: 'categories',
      value: slug,
      label: categoryNameBySlug.get(slug) ?? slug,
    });
  }

  if (filters.free === '1') {
    chips.push({ key: 'free', label: 'Gratuit' });
  }

  const periodLabel = getPeriodLabel(filters.period);
  if (periodLabel) {
    chips.push({ key: 'period', label: periodLabel });
  }

  const timeLabel = getTimeLabel(filters.time);
  if (timeLabel) {
    chips.push({ key: 'time', label: timeLabel });
  }

  if (filters.latitude && filters.longitude) {
    chips.push({ key: 'location', label: 'Autour de moi' });
  }

  if (filters.venue) {
    chips.push({ key: 'venue', label: venueNameBySlug.get(filters.venue) ?? filters.venue });
  }

  return chips;
}

export function getMobileFilterSummary(chips: AppliedFilterChip[]): string {
  if (chips.length === 0) {
    return 'Aucun filtre actif';
  }

  if (chips.length <= 2) {
    return chips.map((chip) => chip.label).join(' · ');
  }

  return `${chips.length} filtres actifs`;
}
